import { Autocomplete, Chip, FormControl, TextField } from '@mui/material';
import React, { useEffect } from 'react';
import {
	Controller,
	FieldValues,
	UseControllerProps,
	useFormContext,
} from 'react-hook-form';
import { SelectFieldConfiguration } from '../../../api/ConfigApi';
import FieldLabel from '../../fields/FieldLabel';

type FieldProps = Omit<SelectFieldConfiguration, 'name'>;
interface Props<T> extends FieldProps, UseControllerProps<T> {
	save?: (currValue: string, fieldName: string) => void;
	fieldHasExternalDataCalls?: boolean;
}

const MultiSelectField = <T extends FieldValues>(props: Props<T>) => {
	const {
		control,
		name,
		title,
		optional,
		values: options,
		disabled,
		save,
		fieldHasExternalDataCalls,
	} = props;

	const { setValue, getValues } = useFormContext();

	// if only one option is available, set it as the value (should only occur once)
	useEffect(() => {
		if (!optional && options?.length === 1 && getValues(name)?.length === 0) {
			setValue(name, [options[0]] as any, { shouldDirty: true });
			// trigger a save to fetch data from external data call, if applicable
			if (fieldHasExternalDataCalls) {
				save && save(options[0], name);
			}
		}
	});

	const disableClearable = !optional && options.length === 1;

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState: { error } }) => (
				<FormControl
					error={!!error && error.type !== 'typeError'}
					sx={{ width: '100%' }}
				>
					<FieldLabel title={title} required={!optional} htmlFor={name} />
					<Autocomplete
						{...field}
						multiple
						onChange={(_event, newValue) => {
							field.onChange(newValue);
						}}
						id={name}
						value={field.value as string[]}
						options={options}
						getOptionLabel={(option) => option}
						disabled={disabled}
						disableClearable={disableClearable}
						blurOnSelect={false}
						openOnFocus={true}
						autoHighlight={true}
						renderTags={(tagValue, getTagProps) =>
							tagValue.map((option, index) => (
								<Chip
									sx={{
										borderRadius: 0,
									}}
									key={index}
									label={option}
									{...getTagProps({ index })}
									onDelete={
										// passing in null will remove the chip's [X] icon
										disableClearable ? null : getTagProps({ index }).onDelete
									}
								/>
							))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								required={!optional}
								// react-hook-form gets mad when there's only one option and it's selected/re-selected
								error={!!error && error.type !== 'typeError'}
								helperText={error?.message}
								disabled={disabled}
								onBlur={(e) => {
									field.onBlur();
									save && save(e.target.value, name);
								}}
								sx={{
									'& .MuiInput-input:focus': {
										backgroundColor: 'transparent',
									},
								}}
							/>
						)}
					/>
				</FormControl>
			)}
		/>
	);
};

export default MultiSelectField;
